import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip,
  ErrorBar
} from 'recharts';
import domtoimage from 'dom-to-image';
import fileDownload from 'js-file-download';
import { useTranslation } from 'react-i18next';

import { FaCamera } from 'react-icons/fa';

import { getNumberInFormat, getXAxisTicks, mapDataToSerie } from '../utils';
import CustomTooltip from './customTooltip';

const COLORS = [ '#3071b3', '#d62728', '#2ca02c', '#9467bd', '#ff7f0e', '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf' ];

const CombinedChart = ({ 
  chartsData,
  selectedVariableName,
  selectedVariableDefinition,
  selectedVariableUnit,
  handleRemoveClick
}) => {
  const marginLeft = 50;
  const marginTop = 30;
  const marginRight = 50;
  const marginBottom = 30;
  
  const width = 1100;
  const height = 484;

  const margin = {
    top: marginTop,
    left: marginLeft,
    bottom: marginBottom,
    right: marginRight
  };

  const { t } = useTranslation();

  const chartRef = React.useRef();

  const yAxisTickFormat = (num) => {
    return getNumberInFormat(num);
  };

  const handleDownload = async () => {
    const blob = await domtoimage.toBlob(chartRef.current, { bgcolor: '#fff' });
    fileDownload(blob, `combined_${selectedVariableName}.png`);
  };

  const renderLegend = () => {
    return (
      <div style={{ textAlign: 'center', marginBottom: '10px' }}>
        {chartsData.map(({ experimentInfo: { pulse, run, machine } }, index) => (
          <span key={`${pulse}-${run}-${machine}`} style={{ 
            display: 'inline-block', 
            margin: '0 10px',
            color: COLORS[index % COLORS.length]
          }}>
            <span style={{ 
              display: 'inline-block', 
              width: '20px', 
              height: '3px', 
              backgroundColor: COLORS[index % COLORS.length],
              marginRight: '5px',
              verticalAlign: 'middle'
            }}></span>
            {`Pulse: ${pulse} Run: ${run} Machine: ${machine}`}
          </span>
        ))}
      </div>
    );
  };

  // Combine all data points by time
  const combinedData = React.useMemo(() => {
    const timePoints = new Set();
    const dataMap = {};

    chartsData.forEach(({ data, experimentInfo: { pulse, run, machine } }, index) => {
      if (data && data.data?.[0]) {
        const serieData = mapDataToSerie(data.data[0]);
        serieData.forEach(point => {
          const timeKey = point.time;
          timePoints.add(timeKey);
          
          if (!dataMap[timeKey]) {
            dataMap[timeKey] = { time: timeKey };
          }
          
          const seriesKey = `series_${index}`;
          dataMap[timeKey][seriesKey] = point.value;
          dataMap[timeKey][`${seriesKey}_error`] = point.error;
        });
      }
    });

    const sortedTimePoints = Array.from(timePoints).sort((a, b) => a - b);
    return sortedTimePoints.map(time => dataMap[time]);
  }, [ chartsData ]);

  const xAxisTicks = getXAxisTicks(combinedData);

  return (
    <div className='chart-wrapper'>
      <button 
        className='download-chart-btn' 
        onClick={handleDownload} 
        title={t('download_chart_button_label')}
      >
        <FaCamera className='camera-icon' />
      </button>
      <div ref={chartRef}>
        {renderLegend()}
        <LineChart width={width} height={height} data={combinedData} margin={margin}>
          <YAxis
            type='number' 
            tickFormatter={yAxisTickFormat} 
            interval={0}
            tick={{ fontSize: 12 }}
            tickMargin={5}
            padding={{ top: 5, bottom: 5 }}
            label={{ 
              value: `${selectedVariableName} [${selectedVariableUnit}]`, 
              angle: -90, 
              position: 'insideLeft',
              style: { textAnchor: 'middle' }
            }}
          />
          <CartesianGrid strokeDasharray='3 3' />
          <Tooltip content={<CustomTooltip />} />
          {chartsData.map(({ experimentInfo: { pulse, run, machine }, id }, index) => {
            const seriesKey = `series_${index}`;
            return (
              <Line
                key={`${pulse}-${run}-${machine}`}
                type='linear' 
                dataKey={seriesKey}
                stroke={COLORS[index % COLORS.length]}
                strokeWidth={2.0}
                isAnimationActive={false}
                name={`Pulse: ${pulse} Run: ${run}`}
              >
                <ErrorBar
                  dataKey={`${seriesKey}_error`}
                  stroke={COLORS[index % COLORS.length]}
                  strokeWidth={1.0} 
                  direction="y"
                  isAnimationActive={false}
                />
              </Line>
            );
          })}
          <XAxis
            type='number'
            dataKey='time'
            tick={{ fontSize: 12 }}
            ticks={xAxisTicks}
            padding={{ left: 5, right: 5 }}
            domain={[ 'dataMin', 'dataMax' ]}
            label={{ value: 'time [s]', position: 'insideBottomRight', offset: -5 }}
          />
        </LineChart>
      </div>
    </div>
  );
};

CombinedChart.propTypes = {
  chartsData: PropTypes.array.isRequired,
  selectedVariableName: PropTypes.string.isRequired,
  selectedVariableDefinition: PropTypes.string.isRequired,
  selectedVariableUnit: PropTypes.string.isRequired,
  handleRemoveClick: PropTypes.func.isRequired
};

export default memo(CombinedChart);
